import React, { useEffect, useState } from "react";
import { Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { Seo } from "@/components/Seo";

export default function FeedbackList() {
  const [items, setItems] = useState([]);

  const load = () => { api.get("/admin/feedback").then((r) => setItems(r.data)); };
  useEffect(load, []);

  const approve = async (id, approved) => {
    await api.put(`/admin/feedback/${id}/approve?approved=${approved}`);
    toast.success(approved ? "Approved for anonymous quoting" : "Set to private");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/feedback/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <AdminShell title="Reader Feedback">
      <Seo title="Reader Feedback" noindex />
      <p className="font-mono-pm text-xs text-stone-500 mb-6">Submissions are private by default. Only approve for quoting when the reader granted permission — quotes are always anonymized.</p>
      <div className="space-y-4">
        {items.map((f) => (
          <div key={f.id} className="border border-stone-900 bg-white p-5" data-testid={`feedback-${f.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="font-serif-display text-lg font-bold text-stone-900">{f.name || "Anonymous"} {f.role && <span className="font-sans-pm text-sm font-normal text-stone-500">· {f.role}</span>}</div>
                <div className="font-mono-pm text-[10px] uppercase tracking-widest text-stone-400 mt-1">{(f.created_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono-pm text-[10px] uppercase px-2 py-1 ${f.approved ? "bg-stone-900 text-[#CCFF00]" : "bg-stone-200 text-stone-600"}`}>{f.approved ? "Approved" : "Private"}</span>
                <span className={`font-mono-pm text-[10px] uppercase px-2 py-1 border ${f.permission_to_quote ? "border-green-700 text-green-700" : "border-stone-300 text-stone-400"}`}>{f.permission_to_quote ? "Quote OK" : "No quote"}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 font-sans-pm text-sm text-stone-700">
              {f.portfolio_type && <Row k="Portfolio" v={f.portfolio_type} />}
              {f.units && <Row k="Units / Sq Ft" v={f.units} />}
              {f.software_used && <Row k="Currently uses" v={f.software_used} />}
              {f.previous_software && <Row k="Previously used" v={f.previous_software} />}
              {f.likes && <Row k="Likes" v={f.likes} full />}
              {f.dislikes && <Row k="Dislikes" v={f.dislikes} full />}
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t border-stone-200">
              {!f.approved ? (
                <button onClick={() => approve(f.id, true)} disabled={!f.permission_to_quote} title={f.permission_to_quote ? "" : "Reader did not grant permission"}
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 font-mono-pm text-[10px] uppercase tracking-widest hover:bg-green-700 disabled:opacity-40"><Check size={12} /> Approve for quoting</button>
              ) : (
                <button onClick={() => approve(f.id, false)} className="inline-flex items-center gap-2 border border-stone-900 px-4 py-2 font-mono-pm text-[10px] uppercase tracking-widest hover:bg-stone-100"><X size={12} /> Make private</button>
              )}
              <button onClick={() => del(f.id)} className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 font-mono-pm text-[10px] uppercase tracking-widest text-stone-500 hover:bg-[#991B1B] hover:text-white hover:border-[#991B1B]"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="border-2 border-dashed border-stone-300 bg-white p-12 text-center font-mono-pm text-sm text-stone-400">No submissions yet.</div>}
      </div>
    </AdminShell>
  );
}

function Row({ k, v, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <span className="font-mono-pm text-[10px] uppercase tracking-widest text-stone-400">{k}: </span>{v}
    </div>
  );
}
